import { DateTime } from 'luxon';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import { SelectTypes } from '@nhl/nhl-breakaway-ui';

import { ConferenceLabel, RankAccessor, StandingsViewType, StandingsViewTypeLabel } from './enums';
import { OrganizationStandings, StandingsSeason, TeamStandingsRecord } from './types';

const ORG_PROPERTY_SUFFIX = 'Name';

export const isDateInRegularSeason = (
    date: DateTime,
    regularSeasonStart?: string,
    regularSeasonEnd?: string
): boolean => {
    if (!date || !regularSeasonStart || !regularSeasonEnd) {
        return false;
    }
    const formattedRegularSeasonStart: DateTime = DateTime.fromISO(regularSeasonStart);
    const formattedRegularSeasonEnd: DateTime = DateTime.fromISO(regularSeasonEnd);

    return (
        date?.hasSame(formattedRegularSeasonStart, 'day') ||
        (date > formattedRegularSeasonStart && date < formattedRegularSeasonEnd) ||
        date?.hasSame(formattedRegularSeasonEnd, 'day')
    );
};

export const getClosestRegularSeasonDate = (
    date: DateTime,
    regularSeasonStart: string | undefined = '',
    regularSeasonEnd: string | undefined = ''
): DateTime => {
    if (!regularSeasonStart || !regularSeasonEnd) {
        return date;
    }
    if (isDateInRegularSeason(date, regularSeasonStart, regularSeasonEnd)) {
        return date;
    }
    const formattedRegularSeasonStartDate: DateTime = DateTime.fromISO(regularSeasonStart);
    const isDateBeforeSeason: boolean = formattedRegularSeasonStartDate > date;
    if (isDateBeforeSeason) {
        return formattedRegularSeasonStartDate;
    }
    return DateTime.fromISO(regularSeasonEnd);
};

export const getSeasonOptionFromSeason = (season: StandingsSeason): SelectTypes.Option<StandingsSeason> => ({
    ...season,
    key: hockeyFormatter.singleSeasonFormatter(season.id),
    label: hockeyFormatter.singleSeasonFormatter(season.id),
});

export const getSeasonFromDate = (
    date: DateTime,
    availableSeasons: Array<StandingsSeason>
): SelectTypes.Option<StandingsSeason> | undefined => {
    let isAfterRegularSeason = false;
    const matchingSeason: StandingsSeason | undefined = availableSeasons?.find(({ standingsEnd, standingsStart }) => {
        const seasonStartDateTime: DateTime = DateTime.fromISO(standingsStart);
        const seasonEndDateTime: DateTime = DateTime.fromISO(standingsEnd);

        // checking against previous season
        const isAfterPreviousRegularSeason: boolean = isAfterRegularSeason;
        isAfterRegularSeason = false;

        if (isAfterPreviousRegularSeason) {
            const isBeforeRegularSeason: boolean = date < seasonStartDateTime;
            if (isBeforeRegularSeason) {
                return true;
            }
        }

        const isWithinRegularSeason: boolean =
            date?.hasSame(seasonStartDateTime, 'day') ||
            (date > seasonStartDateTime && date < seasonEndDateTime) ||
            date?.hasSame(seasonEndDateTime, 'day');
        if (isWithinRegularSeason) {
            return true;
        }
        isAfterRegularSeason = date > seasonEndDateTime;
        return false;
    });
    if (matchingSeason) {
        return getSeasonOptionFromSeason(matchingSeason);
    }
    const mostRecentSeason: StandingsSeason = availableSeasons[availableSeasons.length - 1];
    if (!mostRecentSeason) {
        return;
    }
    return getSeasonOptionFromSeason(mostRecentSeason);
};

export const isValidStandingsViewType = (key: string): boolean => {
    if (!key) {
        return false;
    }
    return !!StandingsViewType[key.toLowerCase()];
};

export const getProperNavigateUrl = (
    date: string,
    season: StandingsSeason | undefined,
    viewType: StandingsViewType,
    isCurrentSeason: boolean,
    wildCardIndicator?: boolean
): string => {
    if (!viewType || !isValidStandingsViewType(viewType)) {
        const defaultViewType = getDefaultStandingsViewTypeForSeason(isCurrentSeason, season, wildCardIndicator);
        return `/standings/${date}/${defaultViewType}`;
    }

    return `/standings/${date}/${viewType}`;
};

export const featuredTeamAbbrev = (): string => window.__SITE_SETTINGS__.franchise.triCode.toUpperCase();

// specifies whether viewed feature is Club Standings, as opposed to League Standings
const isClubStandingsFeature = (): boolean => featuredTeamAbbrev() !== 'NHL';

export const isFeaturedStandingsClub = (record: TeamStandingsRecord): boolean =>
    isClubStandingsFeature() && featuredTeamAbbrev() === record.teamAbbrev.default;

export const getStandingsByOrganization = (
    organizationProperty: StandingsViewTypeLabel,
    standingsData
): OrganizationStandings => {
    const formattedOrganizationProperty: string = organizationProperty?.toLowerCase() + ORG_PROPERTY_SUFFIX;
    const organizedRecords: OrganizationStandings = {};
    standingsData.forEach((record) => {
        // TODO: fix hardcode here
        const organizationId =
            organizationProperty === StandingsViewTypeLabel.league
                ? ConferenceLabel['National Hockey League']
                : record[formattedOrganizationProperty];

        if (organizedRecords[organizationId]) {
            organizedRecords[organizationId].standings.push(record);
        } else {
            organizedRecords[organizationId] = {
                standings: [record],
            };
        }
        // all divisions and conferences involving featured team should appear first
        if (isFeaturedStandingsClub(record)) {
            organizedRecords[organizationId].isAppearingFirst = true;
        }
    });
    return organizedRecords;
};

export const teamRecordsComparator = (
    compareProperty: RankAccessor,
    a: TeamStandingsRecord,
    b: TeamStandingsRecord
): number => parseFloat(a[compareProperty as string]) - parseFloat(b[compareProperty as string]);

export const getWildcardsFromConference = (conference: {
    standings: Array<TeamStandingsRecord>;
}): { divisionLeaders: Array<TeamStandingsRecord>; wildcards: Array<TeamStandingsRecord> } => {
    const divisionLeaders: Array<TeamStandingsRecord> = [];
    const wildcards: Array<TeamStandingsRecord> = [];

    conference.standings.forEach((team) => {
        if (team.wildcardSequence === 0) {
            divisionLeaders.push(team);
        } else {
            wildcards.push(team);
        }
    });

    return { divisionLeaders, wildcards };
};

// sorts conferences or divisions to appear first if they are specified to, otherwise is a neutral sort if none of the orgs are specified as such
export const getSortedOrganizations = (organizedRecords: OrganizationStandings): Array<string> => {
    if (!isClubStandingsFeature()) {
        return Object.keys(organizedRecords).sort();
    }

    return Object.keys(organizedRecords).sort((orgName1, orgName2): number => {
        if (organizedRecords[orgName1]?.isAppearingFirst) {
            return -1;
        }
        if (organizedRecords[orgName2]?.isAppearingFirst) {
            return 1;
        }
        return 0;
    });
};

export const isCurrentSeason = (
    selectedSeason: StandingsSeason | undefined,
    currentSeason: StandingsSeason | undefined
): boolean => (selectedSeason && currentSeason ? selectedSeason.id === currentSeason.id : false);

export const getDefaultStandingsViewTypeForSeason = (
    isCurrentSeason: boolean,
    season?: StandingsSeason,
    wildCardIndicator?: boolean
): StandingsViewType => {
    // The current season should show the wildcard view if it is in use, and the wildcardIndicator is passed
    if (isCurrentSeason && season?.wildcardInUse && wildCardIndicator) {
        return StandingsViewType.wildcard;
    }

    // Otherwise, for any season, the division view should be shown if in use
    if (season?.divisionsInUse) {
        return StandingsViewType.division;
    }

    // Otherwise, the conference view should be shown if in use...
    if (season?.conferencesInUse) {
        return StandingsViewType.conference;
    }

    // Otherwise, show league view (available for all seasons)
    return StandingsViewType.league;
};

export const isValidViewForSeason = (viewType: StandingsViewType, season: StandingsSeason): boolean => {
    if (viewType === StandingsViewType.conference && !season.conferencesInUse) {
        return false;
    }

    if (viewType === StandingsViewType.division && !season.divisionsInUse) {
        return false;
    }

    if (viewType === StandingsViewType.wildcard && !season.wildcardInUse) {
        return false;
    }

    return true;
};
